import { useState } from 'react';
import { Mail, Send, Clock, CheckCircle, XCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { fetchWithAuth } from '../../utils/authUtils';

interface Invitation {
  invitationId: number;
  email: string;
}

const InvitationsView = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sentInvitations, setSentInvitations] = useState<Invitation[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email.trim()) {
      setError('El correo electrónico es requerido');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.createInvitation, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSentInvitations(prev => [
          { invitationId: data.invitationId, email: email.trim() },
          ...prev,
        ]);
        setSuccessMessage(`Invitación enviada exitosamente a ${email.trim()}`);
        setEmail('');
      } else {
        setError(data.message || 'Error al enviar la invitación');
      }
    } catch {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Mail size={28} className="text-[#144A7B]" />
        <div>
          <h2 className="text-xl font-bold text-gray-800">Invitaciones de Registro</h2>
          <p className="text-sm text-gray-500">
            Envía un enlace único de registro a nuevos clientes
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Clock size={20} className="text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-800">
          Cada enlace es de <strong>uso único</strong> y expira en <strong>48 horas</strong>.
          El cliente recibirá un correo con su enlace personalizado de registro.
        </p>
      </div>

      {/* Formulario */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-700 mb-4">Enviar nueva invitación</h3>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <XCircle size={18} />
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <CheckCircle size={18} />
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="correo@empresa.cl"
            required
            disabled={submitting}
            className="flex-1 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 bg-[#144A7B] hover:bg-[#0d3354] text-white font-semibold px-5 py-2 rounded transition disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Send size={18} />
            {submitting ? 'Enviando...' : 'Enviar Invitación'}
          </button>
        </form>
      </div>

      {/* Historial de invitaciones enviadas en esta sesión */}
      {sentInvitations.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-700 mb-3">
            Enviadas en esta sesión ({sentInvitations.length})
          </h3>
          <div className="space-y-2">
            {sentInvitations.map((inv) => (
              <div
                key={inv.invitationId}
                className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle size={18} className="text-green-500" />
                  <span className="text-gray-800">{inv.email}</span>
                </div>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                  ID #{inv.invitationId}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationsView;
